<template>
  <div class="body bank-app">
    <Navbar :show-back-button="true" @back="navigateToDashboard"/>
    
    <div class="section-header">
      <!-- <button class="back-arrow" @click="navigateToDashboard">
        →
      </button> -->
      <h3 class="section-title mt-3" style="margin-right: 10px;">{{ $t('employer.reportMande1.title') }}</h3>
    </div>
    
    <!-- Report Controls -->
    <div class="action-bar">
      <div style="display: inline-flex; gap: 2px;">
      <button 
        class="refresh-btn" 
        @click="refreshCurrentTab"
        :disabled="loading"
        :title="$t('employer.reportMande1.refresh')"
      >
        🔄
        <span class="refresh-text">
          {{ $t('employer.reportMande1.refresh') }}
        </span>
      </button>
  
        <!-- Print Button -->
        <button class="print-btn" @click="printReport">
          🖨️ 
          <span class="print-text">{{ $t('employer.reportMande1.print') }}</span>
        </button>
  
        <!-- Export Button -->
        <button class="export-btn" @click="exportToExcel">
          📊
          <span class="export-text">{{ $t('employer.reportMande1.export') }}</span>
        </button>
      </div>

      <!-- Currency Selector -->
      <div class="currency-selector">
        <label for="currency-select" class="currency-label">{{ $t('employer.reportMande1.currency') }}</label>
        <select 
          id="currency-select"
          v-model="selectedCurrency" 
          @change="loadReportData"
          class="currency-select"
          :disabled="loadingArz || arzList.length === 0"
        >
          <option value="0">{{ $t('employer.reportMande1.all_to_rial') }}</option>
          <option 
            v-for="arz in arzList" 
            :key="arz.idd" 
            :value="arz.idd"
          >
            {{ arz.nam }} ({{ arz.sign }})
          </option>
        </select>
        <div v-if="loadingArz" class="currency-loading">
          {{ $t('employer.reportMande1.loading_currencies') }}
        </div>
      </div>

      <!-- Search Input -->
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery"
          :placeholder="$t('employer.reportMande1.search_customer')"
          class="search-input"
          @input="onSearch"
        />
        <span class="search-icon">🔍</span>
      </div>
    </div>

    <!-- Report Tabs -->
    <div class="report-tabs">
      <div class="tab-buttons">
        <button 
          class="tab-button"
          style="background-color: rgba(247, 54, 54, 0.308);"
          :class="{ active: activeTab === 'bed' }"
          @click="handleTabClick('bed')"
        >
          {{ $t('employer.reportMande1.debtors') }}
          <span v-if="bedReport.data?.length" class="tab-count">
            ({{ bedReport.data.length }})
          </span>
        </button>
        <button 
          class="tab-button"
          style="background-color: rgba(23, 245, 23, 0.323);"
          :class="{ active: activeTab === 'bes' }"
          @click="handleTabClick('bes')"
        >
          {{ $t('employer.reportMande1.creditors') }}
          <span v-if="besReport.data?.length" class="tab-count">
            ({{ besReport.data.length }})
          </span>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <span>{{ $t('employer.reportMande1.loading_report') }}</span>
      </div>

      <!-- Error State -->
      <div v-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <h4>{{ $t('employer.reportMande1.error_title') }}</h4>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="loadReportData">
          {{ $t('employer.reportMande1.retry') }}
        </button>
      </div>

        <!-- Report Content -->
        <div v-if="!loading && !error" class="report-content">
        <!-- Bed Tab Content -->
        <div v-if="activeTab === 'bed'" class="tab-content">
            <div class="report-header">
            <h4>{{ $t('employer.reportMande1.debtors_list') }}</h4>
            <div class="report-info">
                <span>{{ $t('employer.reportMande1.total_count') }}: {{ bedReport.total_records || 0 }}</span>
                <span> | </span>
                <span>{{ $t('employer.reportMande1.total_amount') }}: {{ formatCurrency(bedReport.total_bed || 0) }}</span>
            </div>
            <!-- Bed Sorting Controls -->
            <div class="sort-controls">
                <select v-model="bedSortField" @change="bedSortOrder = 'desc'" class="sort-select">
                <option value="M_Bed">{{ $t('employer.reportMande1.sort_by_amount') }}</option>
                <option value="Title">{{ $t('employer.reportMande1.sort_by_name') }}</option>
                <option value="Ei5">{{ $t('employer.reportMande1.sort_by_code') }}</option>
                <option value="M_Bed_A">{{ $t('employer.reportMande1.sort_by_foreign_amount') }}</option>
                <option value="ted_roz_bed">{{ $t('employer.reportMande1.sort_by_days') }}</option>
                </select>
                <button @click="bedSortOrder = bedSortOrder === 'asc' ? 'desc' : 'asc'" class="sort-order-btn">
                {{ bedSortOrder === 'asc' ? $t('employer.reportMande1.ascending') : $t('employer.reportMande1.descending') }}
                </button>
            </div>
            </div>

            <div v-if="filteredBedData.length === 0" class="empty-state">
            <span>{{ $t('employer.reportMande1.no_debtors_found') }}</span>
            </div>

            <div v-else class="customer-cards-container">
              <div class="customer-cards-grid">
                <div v-for="customer in paginatedBedData" :key="customer.Ei5" class="customer-card">
                  <div class="card-body">
                    <!-- First Row: نام and تلفن -->
                    <div class="row first-row">
                      <div class="duration-section">
                        <span class="duration-label">{{ $t('employer.reportMande1.name') }}:</span>
                        <span class="duration-value">{{ customer.Title || $t('employer.reportMande1.not_registered') }}</span>
                        
                        <span class="duration-label" style="margin-right: auto;">{{ $t('employer.reportMande1.phone') }}:</span>
                        <span class="duration-value">{{ customer.Tel2 || $t('employer.reportMande1.not_registered') }}</span>
                      </div>
                    </div>
                    
                    <!-- Second Row: مبلغ and بدهکار/بستانکار -->
                    <div class="row second-row">
                      <div class="amount-section">
                        <span class="amount-label">{{ $t('employer.reportMande1.amount') }} ({{ getCurrencyName(selectedCurrency) }}):</span>
                        <span v-if="selectedCurrency==1" class="amount-value" :class="{'bed-amount': customer.M_Bed > 0, 'bes-amount': customer.M_Bed <= 0}">
                          {{ formatCurrency(Math.abs(customer.M_Bed > 0 ? customer.M_Bed : customer.M_Bes)) }}
                        </span>
                        <span v-else class="amount-value" style="text-align: left; margin-right: auto;" :class="{'bed-amount': customer.M_Bed > 0, 'bes-amount': customer.M_Bed <= 0}">
                          {{ formatCurrency(Math.abs(customer.M_Bed_A > 0 ? customer.M_Bed_A : customer.M_Bes_A)) }}
                        </span>
                        <span class="amount-status" style="margin-right: auto;" :class="'status-bed'">
                          {{ $t('employer.reportMande1.debtor') }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Third Row: نرخ امروز (Today's Rate) -->
                    <div v-if="selectedCurrency != 0 && selectedCurrency != 1" class="row third-row">
                      <div class="rate-section">
                        <span class="rate-label">{{ $t('employer.reportMande1.today_rate') }}:</span>
                        <span v-if="currencyPricesLoading" class="rate-value loading-rate">
                          {{ $t('employer.reportMande1.loading_rate') }}
                        </span>
                        <span v-else-if="getTodayRate > 1" class="rate-value">
                          {{ formatCurrency(getTodayRate) }}
                        </span>
                        <span v-else class="rate-value">
                          {{ formatCurrency(1) }}
                        </span>
                      </div>
                    </div>

                    <!-- Fourth Row: ارزش امروز به ریال (Today's Value in Rial) -->
                    <div v-if="selectedCurrency != 0 && selectedCurrency != 1" class="row fourth-row">
                      <div class="rate-section">
                        <span class="rate-label">{{ $t('employer.reportMande1.today_value_rial') }}:</span>
                        <span v-if="currencyPricesLoading" class="rate-value loading-rate">
                          {{ $t('employer.reportMande1.calculating') }}
                        </span>
                        <span v-else class="rate-value">
                          {{ formatCurrency(
                            Math.abs(
                              (customer.M_Bed_A > 0 ? customer.M_Bed_A : customer.M_Bes_A) 
                              * getTodayRate
                            )
                          ) }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Fourth Row: مدت بدهکار و بستانکار -->
                    <div class="row fourth-row">
                      <div class="rate-section">
                        <div class="duration-section">
                          <span class="duration-label">{{ $t('employer.reportMande1.debtor_days') }}:</span>
                          <span class="duration-value" style="color: red;">{{ customer.ted_roz_bed || 0 }} {{ $t('employer.reportMande1.days') }}</span>
                          
                          <span class="duration-label" style="margin-right: auto;">{{ $t('employer.reportMande1.creditor_days') }}:</span>
                          <span class="duration-value" style="color: green;">{{ customer.ted_roz_bes || 0 }} {{ $t('employer.reportMande1.days') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bed Pagination -->
            <div v-if="filteredBedData.length > itemsPerPage" class="pagination-controls">
            <button 
                @click="bedPrevPage" 
                :disabled="bedCurrentPage === 1"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.previous') }}
            </button>
            <span class="page-info">
                {{ $t('employer.reportMande1.page') }} {{ bedCurrentPage }} {{ $t('employer.reportMande1.of') }} {{ bedTotalPages }}
            </span>
            <button 
                @click="bedNextPage" 
                :disabled="bedCurrentPage === bedTotalPages"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.next') }}
            </button>
            </div>
        </div>

        <!-- Bes Tab Content -->
        <div v-else-if="activeTab === 'bes'" class="tab-content">
            <div class="report-header">
            <h4>{{ $t('employer.reportMande1.creditors_list') }}</h4>
            <div class="report-info">
                <span>{{ $t('employer.reportMande1.total_count') }}: {{ besReport.total_records || 0 }}</span>
                <span> | </span>
                <span>{{ $t('employer.reportMande1.total_amount') }}: {{ formatCurrency(besReport.total_bes || 0) }}</span>
            </div>
            <!-- Bes Sorting Controls -->
            <div class="sort-controls">
                <select v-model="besSortField" @change="besSortOrder = 'desc'" class="sort-select">
                <option value="M_Bes">{{ $t('employer.reportMande1.sort_by_amount') }}</option>
                <option value="Title">{{ $t('employer.reportMande1.sort_by_name') }}</option>
                <option value="Ei5">{{ $t('employer.reportMande1.sort_by_code') }}</option>
                <option value="M_Bes_A">{{ $t('employer.reportMande1.sort_by_foreign_amount') }}</option>
                <option value="ted_roz_bes">{{ $t('employer.reportMande1.sort_by_days') }}</option>
                </select>
                <button @click="besSortOrder = besSortOrder === 'asc' ? 'desc' : 'asc'" class="sort-order-btn">
                {{ besSortOrder === 'asc' ? $t('employer.reportMande1.ascending') : $t('employer.reportMande1.descending') }}
                </button>
            </div>
            </div>

            <div v-if="filteredBesData.length === 0" class="empty-state">
            <span>{{ $t('employer.reportMande1.no_creditors_found') }}</span>
            </div>

            <div v-else class="customer-cards-container">
              <div class="customer-cards-grid">
                <div v-for="customer in paginatedBesData" :key="customer.Ei5" class="customer-card">
                  <div class="card-body">
                    <!-- First Row: نام and تلفن -->
                    <div class="row first-row">
                      <div class="duration-section">
                        <span class="duration-label">{{ $t('employer.reportMande1.name') }}:</span>
                        <span class="duration-value">{{ customer.Title || $t('employer.reportMande1.not_registered') }}</span>
                        
                        <span class="duration-label" style="margin-right: auto;">{{ $t('employer.reportMande1.phone') }}:</span>
                        <span class="duration-value">{{ customer.Tel2 || $t('employer.reportMande1.not_registered') }}</span>
                      </div>
                    </div>
                    
                    <!-- Second Row: مبلغ and بدهکار/بستانکار -->
                    <div class="row second-row">
                      <div class="amount-section">
                        <span class="amount-label">{{ $t('employer.reportMande1.amount') }} ({{ getCurrencyName(selectedCurrency) }}):</span>
                        <span v-if="selectedCurrency==1" class="amount-value" :class="{'bed-amount': customer.M_Bed > 0, 'bes-amount': customer.M_Bed <= 0}">
                          {{ formatCurrency(Math.abs(customer.M_Bed > 0 ? customer.M_Bed : customer.M_Bes)) }}
                        </span>
                        <span v-else class="amount-value" style="text-align: left; margin-right: auto;" :class="{'bed-amount': customer.M_Bed > 0, 'bes-amount': customer.M_Bed <= 0}">
                          {{ formatCurrency(Math.abs(customer.M_Bed_A > 0 ? customer.M_Bed_A : customer.M_Bes_A)) }}
                        </span>
                        <span class="amount-status" style="margin-right: auto;" :class="{'status-bed': customer.M_Bed > 0, 'status-bes': customer.M_Bed <= 0}">
                          {{ $t('employer.reportMande1.creditor') }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Third Row: نرخ امروز (Today's Rate) -->
                    <div v-if="selectedCurrency != 0 && selectedCurrency != 1" class="row third-row">
                      <div class="rate-section">
                        <span class="rate-label">{{ $t('employer.reportMande1.today_rate') }}:</span>
                        <span v-if="currencyPricesLoading" class="rate-value loading-rate">
                          {{ $t('employer.reportMande1.loading_rate') }}
                        </span>
                        <span v-else-if="getTodayRate > 1" class="rate-value">
                          {{ formatCurrency(getTodayRate) }}
                        </span>
                        <span v-else class="rate-value">
                          {{ formatCurrency(1) }}
                        </span>
                      </div>
                    </div>

                    <!-- Fourth Row: ارزش امروز به ریال (Today's Value in Rial) -->
                    <div v-if="selectedCurrency != 0 && selectedCurrency != 1" class="row fourth-row">
                      <div class="rate-section">
                        <span class="rate-label">{{ $t('employer.reportMande1.today_value_rial') }}:</span>
                        <span v-if="currencyPricesLoading" class="rate-value loading-rate">
                          {{ $t('employer.reportMande1.calculating') }}
                        </span>
                        <span v-else class="rate-value">
                          {{ formatCurrency(
                            Math.abs(
                              (customer.M_Bed_A > 0 ? customer.M_Bed_A : customer.M_Bes_A) 
                              * getTodayRate
                            )
                          ) }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Fourth Row: مدت بدهکار و بستانکار -->
                    <div class="row fourth-row">
                      <div class="rate-section">
                        <div class="duration-section">
                          <span class="duration-label">{{ $t('employer.reportMande1.debtor_days') }}:</span>
                          <span class="duration-value" style="color: red;">{{ customer.ted_roz_bed || 0 }} {{ $t('employer.reportMande1.days') }}</span>
                          
                          <span class="duration-label" style="margin-right: auto;">{{ $t('employer.reportMande1.creditor_days') }}:</span>
                          <span class="duration-value" style="color: green;">{{ customer.ted_roz_bes || 0 }} {{ $t('employer.reportMande1.days') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bes Pagination -->
            <div v-if="filteredBesData.length > itemsPerPage" class="pagination-controls">
            <button 
                @click="besPrevPage" 
                :disabled="besCurrentPage === 1"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.previous') }}
            </button>
            <span class="page-info">
                {{ $t('employer.reportMande1.page') }} {{ besCurrentPage }} {{ $t('employer.reportMande1.of') }} {{ besTotalPages }}
            </span>
            <button 
                @click="besNextPage" 
                :disabled="besCurrentPage === besTotalPages"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.next') }}
            </button>
            </div>
        </div>

        <!-- All Tab Content -->
        <div v-else-if="activeTab === 'all'" class="tab-content">
            <div class="report-header">
            <h4>{{ $t('employer.reportMande1.all_customers_list') }}</h4>
            <div class="report-info">
                <span>{{ $t('employer.reportMande1.total_count') }}: {{ allCustomersData.length }}</span>
                <span> | </span>
                <span>{{ $t('employer.reportMande1.total_balance') }}: {{ formatCurrency(totalBalance) }}</span>
            </div>
            <!-- All Sorting Controls -->
            <div class="sort-controls">
                <select v-model="allSortField" @change="allSortOrder = 'desc'" class="sort-select">
                <option value="balance">{{ $t('employer.reportMande1.sort_by_balance') }}</option>
                <option value="Title">{{ $t('employer.reportMande1.sort_by_name') }}</option>
                <option value="Ei5">{{ $t('employer.reportMande1.sort_by_code') }}</option>
                <option value="M_Bed">{{ $t('employer.reportMande1.sort_by_debtor') }}</option>
                <option value="M_Bes">{{ $t('employer.reportMande1.sort_by_creditor') }}</option>
                </select>
                <button @click="allSortOrder = allSortOrder === 'asc' ? 'desc' : 'asc'" class="sort-order-btn">
                {{ allSortOrder === 'asc' ? $t('employer.reportMande1.ascending') : $t('employer.reportMande1.descending') }}
                </button>
            </div>
            </div>

            <div v-if="filteredAllData.length === 0" class="empty-state">
            <span>{{ $t('employer.reportMande1.no_customers_found') }}</span>
            </div>

            <div v-else class="customer-cards-container">
            <div class="customer-cards-grid">
                <div v-for="customer in paginatedAllData" :key="customer.Ei5" class="customer-card">
                <div class="card-header">
                    <h3 class="customer-name">{{ customer.Title }}</h3>                    
                </div>
                
                <div class="card-body">
                    <div class="customer-balance-section">
                    <div class="balance-item main-balance">
                        <span class="balance-label">{{ $t('employer.reportMande1.balance') }}:</span>
                        <span class="balance-value" :class="getBalanceClass(customer)">
                        {{ formatCurrency(customer.balance) }}
                        </span>
                    </div>
                    
                    <div class="balance-details">
                        <div v-if="customer.M_Bed" class="balance-detail-item">
                        <span class="detail-label">{{ $t('employer.reportMande1.debtor') }}:</span>
                        <span class="detail-value bed-amount">
                            {{ formatCurrency(customer.M_Bed) }}
                        </span>
                        </div>
                        
                        <div v-if="customer.M_Bes" class="balance-detail-item">
                        <span class="detail-label">{{ $t('employer.reportMande1.creditor') }}:</span>
                        <span class="detail-value bes-amount">
                            {{ formatCurrency(customer.M_Bes) }}
                        </span>
                        </div>
                    </div>
                    </div>
                    
                    <div class="customer-info-section">
                    <div class="info-row">
                        <span class="info-label">{{ $t('employer.reportMande1.phone') }}:</span>
                        <span class="info-value">{{ customer.Tel2 || $t('employer.reportMande1.not_registered') }}</span>
                    </div>
                    
                    <div v-if="customer.Mobile" class="info-row">
                        <span class="info-label">{{ $t('employer.reportMande1.mobile') }}:</span>
                        <span class="info-value">{{ customer.Mobile }}</span>
                    </div>
                    </div>
                    
                    <div class="customer-stats-section">
                    <div class="stats-grid">
                        <div v-if="customer.ted_roz_bed" class="stat-item bed-stat">
                        <span class="stat-icon">📅</span>
                        <div class="stat-content">
                            <span class="stat-label">{{ $t('employer.reportMande1.debtor_days') }}</span>
                            <span class="stat-value">{{ customer.ted_roz_bed }}</span>
                        </div>
                        </div>
                        
                        <div v-if="customer.ted_roz_bes" class="stat-item bes-stat">
                        <span class="stat-icon">📅</span>
                        <div class="stat-content">
                            <span class="stat-label">{{ $t('employer.reportMande1.creditor_days') }}</span>
                            <span class="stat-value">{{ customer.ted_roz_bes }}</span>
                        </div>
                        </div>
                    </div>
                    </div>
                </div>
                </div>
            </div>
            </div>

            <!-- All Pagination -->
            <div v-if="filteredAllData.length > itemsPerPage" class="pagination-controls">
            <button 
                @click="allPrevPage" 
                :disabled="allCurrentPage === 1"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.previous') }}
            </button>
            <span class="page-info">
                {{ $t('employer.reportMande1.page') }} {{ allCurrentPage }} {{ $t('employer.reportMande1.of') }} {{ allTotalPages }}
            </span>
            <button 
                @click="allNextPage" 
                :disabled="allCurrentPage === allTotalPages"
                class="pagination-btn"
            >
                {{ $t('employer.reportMande1.next') }}
            </button>
            </div>
        </div>
        </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { accountingService, employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'

export default {
  name: 'ReportMande',
  components: {
    Navbar
  },
  setup() {
    const router = useRouter()
    
    // Data states
    const loading = ref(false)
    const loadingArz = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const activeTab = ref('bed')

    // Currency price states
    const currencyPrices = ref([])
    const currencyPricesLoading = ref(false)
    
    // Reports data
    const bedReport = ref({
      data: [],
      total_records: 0,
      total_bed: 0,
      total_customers: 0
    })
    
    const besReport = ref({
      data: [],
      total_records: 0,
      total_bes: 0,
      total_customers: 0
    })
    
    // Currency states
    const arzList = ref([])
    const selectedCurrency = ref(1) // 0 = all currencies
    
    // Pagination states
    const itemsPerPage = 10
    const bedCurrentPage = ref(1)
    const besCurrentPage = ref(1)
    const allCurrentPage = ref(1)
    
    // Sorting states
    const bedSortField = ref('M_Bed')
    const bedSortOrder = ref('desc')
    const besSortField = ref('M_Bes')
    const besSortOrder = ref('desc')
    const allSortField = ref('balance')
    const allSortOrder = ref('desc')

    const loadedReports = ref({
      bed: false,  // Has bed report been loaded?
      bes: false,  // Has bes report been loaded?
      all: false   // Has all report been loaded? (will be auto-calculated)
    })
    
    // Modal states
    const showCustomerModal = ref(false)
    const selectedCustomer = ref(null)
    
    // Fetch currencies
    const fetchAllArz = async () => {
      loadingArz.value = true
      try {
        console.log('🔧 Fetching all ARZ for mande report...')
        const response = await accountingService.getAllArz()
        if (response.data.success) {
          arzList.value = response.data.data
          console.log('✅ ARZ list loaded:', arzList.value)
        } else {
          console.error('❌ Failed to load ARZ:', response.data.message)
        }
      } catch (err) {
        console.error('❌ Error fetching ARZ:', err)
      } finally {
        loadingArz.value = false
      }
    }
    
    // Load report data with lazy loading support
    const loadReportData = async (forceReload = false, specificTab = null) => {
      // If specificTab is provided, we're loading that tab
      // Otherwise, we're loading based on activeTab
      
      const tabToLoad = specificTab || activeTab.value
      const tabKey = tabToLoad === 'all' ? 'all' : tabToLoad
      
      // Check if we already have this data (and not forcing reload)
      if (!forceReload && loadedReports.value[tabKey]) {
        console.log(`✅ ${tabToLoad} data already loaded, skipping...`)
        return
      }
      
      loading.value = true
      error.value = null
      
      try {
        console.log(`📊 Loading ${tabToLoad} report data...`)

        // Fetch currency prices if needed
        if (selectedCurrency.value !== 0 && selectedCurrency.value !== 1) {
          await fetchCurrencyPrices()
        }
        
        // Based on which tab we're loading
        switch (tabToLoad) {
          case 'bed':
            await loadBedReport()
            loadedReports.value.bed = true
            break
            
          case 'bes':
            await loadBesReport()
            loadedReports.value.bes = true
            break
            
          case 'all':
            // For 'all' tab, we need both bed and bes reports
            if (!loadedReports.value.bed) {
              await loadBedReport()
              loadedReports.value.bed = true
            }
            if (!loadedReports.value.bes) {
              await loadBesReport()
              loadedReports.value.bes = true
            }
            loadedReports.value.all = true
            break
        }
        
      } catch (err) {
        console.error('❌ Error loading mande report:', err)
        error.value = err.response?.data?.detail || 'خطا در بارگذاری گزارش'
      } finally {
        loading.value = false
      }
    }

    // Separate function to load bed report
    const loadBedReport = async () => {
      const bedRequest = {
        arz: selectedCurrency.value,
        bedorBes: 1 // 1 = debit
      }
      
      console.log('📊 Fetching bed report with params:', bedRequest)
      const bedResponse = await employerService.getMandeReport(bedRequest)
      
      if (bedResponse.data.success) {
        bedReport.value = bedResponse.data
        console.log('✅ Bed report loaded:', bedReport.value.data.length, 'records')
      } else {
        console.error('❌ Failed to load bed report:', bedResponse.data.message)
        throw new Error(bedResponse.data.message || 'خطا در بارگذاری گزارش بدهکاران')
      }
    }

    // Separate function to load bes report
    const loadBesReport = async () => {
      const besRequest = {
        arz: selectedCurrency.value,
        bedorBes: 2 // 2 = credit
      }
      
      console.log('📊 Fetching bes report with params:', besRequest)
      const besResponse = await employerService.getMandeReport(besRequest)
      
      if (besResponse.data.success) {
        besReport.value = besResponse.data
        console.log('✅ Bes report loaded:', besReport.value.data.length, 'records')
      } else {
        console.error('❌ Failed to load bes report:', besResponse.data.message)
        throw new Error(besResponse.data.message || 'خطا در بارگذاری گزارش بستانکاران')
      }
    }
    
    // Combined all customers data
    const allCustomersData = computed(() => {
      const allData = []
      
      // Add bed customers
      bedReport.value.data?.forEach(customer => {
        allData.push({
          ...customer,
          balance: customer.M_Bed || 0,
          balanceType: 'bed'
        })
      })
      
      // Add bes customers
      besReport.value.data?.forEach(customer => {
        allData.push({
          ...customer,
          balance: customer.M_Bes || 0,
          balanceType: 'bes'
        })
      })
      
      return allData
    })
    
    // Total balance
    const totalBalance = computed(() => {
      const bedTotal = bedReport.value.total_bed || 0
      const besTotal = besReport.value.total_bes || 0
      return bedTotal - besTotal
    })
    
    // Get balance status
    const getBalanceStatusClass = computed(() => {
      if (totalBalance.value > 0) return 'balance-status-bed'
      if (totalBalance.value < 0) return 'balance-status-bes'
      return 'balance-status-zero'
    })
    
    const getBalanceStatusText = computed(() => {
      if (totalBalance.value > 0) return 'بدهکار'
      if (totalBalance.value < 0) return 'بستانکار'
      return 'متوازن'
    })
    
    const getTotalBalanceClass = computed(() => {
      if (totalBalance.value > 0) return 'bed-amount'
      if (totalBalance.value < 0) return 'bes-amount'
      return ''
    })
    
    // Filter functions
    const onSearch = () => {
      // Reset pagination when searching
      bedCurrentPage.value = 1
      besCurrentPage.value = 1
      allCurrentPage.value = 1
    }
    
    const filteredBedData = computed(() => {
      let data = bedReport.value.data || []
      
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        data = data.filter(customer => 
          customer.Title?.toLowerCase().includes(query) ||
          customer.Ei5?.toString().includes(query) ||
          customer.Tel2?.includes(query) ||
          customer.Code?.toLowerCase().includes(query)
        )
      }
      
      // Apply sorting
      return sortData(data, bedSortField.value, bedSortOrder.value)
    })
    
    const filteredBesData = computed(() => {
      let data = besReport.value.data || []
      
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        data = data.filter(customer => 
          customer.Title?.toLowerCase().includes(query) ||
          customer.Ei5?.toString().includes(query) ||
          customer.Tel2?.includes(query) ||
          customer.Code?.toLowerCase().includes(query)
        )
      }
      
      // Apply sorting
      return sortData(data, besSortField.value, besSortOrder.value, 'bes')
    })
    
    const filteredAllData = computed(() => {
      let data = allCustomersData.value
      
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        data = data.filter(customer => 
          customer.Title?.toLowerCase().includes(query) ||
          customer.Ei5?.toString().includes(query) ||
          customer.Tel2?.includes(query) ||
          customer.Code?.toLowerCase().includes(query)
        )
      }
      
      // Apply sorting
      return sortData(data, allSortField.value, allSortOrder.value, 'all')
    })

    // Fetch currency prices
    const fetchCurrencyPrices = async () => {
      if (selectedCurrency.value === 0 || selectedCurrency.value === 1) {
        // No need to fetch for Rial or all currencies
        return
      }
      
      currencyPricesLoading.value = true
      try {
        console.log('💰 Fetching currency prices...')
        const response = await employerService.getCurrenciesPrice()
        if (response.data && response.data.success) {
          currencyPrices.value = response.data.currency_prices || []
          console.log('✅ Currency prices loaded:', currencyPrices.value, 'items')
        }
      } catch (err) {
        console.error('❌ Error fetching currency prices:', err)
      } finally {
        currencyPricesLoading.value = false
      }
    }

    // Get today's rate based on selected currency
    const getTodayRate = computed(() => {
      if (selectedCurrency.value === 0 || selectedCurrency.value === 1) {
        // For Rial (1) or all currencies (0), no conversion needed
        return 1
      }
      
      if (!currencyPrices.value || currencyPrices.value.length === 0) {
        return 1 // Default rate if no prices loaded
      }
      
      // Get currency code from ID
      const currencyCode = getCurrencyCodeFromId(selectedCurrency.value)
      if (!currencyCode) {
        console.warn('⚠️ Currency code not found for ID:', selectedCurrency.value)
        return 1
      }
      
      // Find the currency in the prices list
      const currency = currencyPrices.value.find(cp => {
        if (!cp || !cp['کد ارز']) return false
        
        // Normalize both codes for comparison
        const dbCode = cp['کد ارز'].toString().trim().toUpperCase()
        const targetCode = currencyCode.toString().trim().toUpperCase()
        
        return dbCode === targetCode
      })
      
      if (currency && currency['قیمت لحظه‌ای']) {
        try {
          // Convert price to number (remove Persian/English commas and convert)
          const priceStr = currency['قیمت لحظه‌ای'].toString()
            .replace(/[,\u066B\u066C\u0660-\u0669\u06F0-\u06F9]/g, (match) => {
              // Handle Persian digits and separators
              if (match === '\u066B' || match === '\u066C' || match === ',') {
                return '' // Remove thousand separators
              }
              // Convert Persian digits to English digits
              const persianDigits = ['\u0660', '\u0661', '\u0662', '\u0663', '\u0664', 
                                    '\u0665', '\u0666', '\u0667', '\u0668', '\u0669',
                                    '\u06F0', '\u06F1', '\u06F2', '\u06F3', '\u06F4',
                                    '\u06F5', '\u06F6', '\u06F7', '\u06F8', '\u06F9'];
              const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                                    '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
              const index = persianDigits.indexOf(match);
              return index !== -1 ? englishDigits[index] : match;
            })
          
          const rate = parseFloat(priceStr) * 10
          return !isNaN(rate) && rate > 0 ? rate : 1
        } catch (error) {
          console.error('❌ Error parsing currency rate:', error)
          return 1
        }
      }
      
      console.warn('⚠️ Currency price not found for code:', currencyCode)
      return 1 // Default rate if not found
    })

    // Helper to get currency code from ID
    const getCurrencyCodeFromId = (id) => {
      const currency = arzList.value.find(arz => arz.idd == id)
      return currency ? currency.sign : null
    }

    // Helper to get currency name from ID
    const getCurrencyNameFromId = (id) => {
      if (id === 0 || id === "0") return 'همه ارزها'
      if (id === 1 || id === "1") return 'ریال'
      
      const currency = arzList.value.find(arz => arz.idd == id)
      return currency ? currency.nam : 'ریال'
    }
    
    // Sorting function
    const sortData = (data, field, order, type = 'bed') => {
      return [...data].sort((a, b) => {
        let aValue, bValue
        
        switch (field) {
          case 'Ei5':
            aValue = a.Ei5
            bValue = b.Ei5
            break
          case 'Title':
            aValue = a.Title || ''
            bValue = b.Title || ''
            break
          case 'M_Bed':
            aValue = a.M_Bed || 0
            bValue = b.M_Bed || 0
            break
          case 'M_Bes':
            aValue = a.M_Bes || 0
            bValue = b.M_Bes || 0
            break
          case 'M_Bed_A':
            aValue = a.M_Bed_A || 0
            bValue = b.M_Bed_A || 0
            break
          case 'M_Bes_A':
            aValue = a.M_Bes_A || 0
            bValue = b.M_Bes_A || 0
            break
          case 'ted_roz_bed':
            aValue = a.ted_roz_bed || 0
            bValue = b.ted_roz_bed || 0
            break
          case 'ted_roz_bes':
            aValue = a.ted_roz_bes || 0
            bValue = b.ted_roz_bes || 0
            break
          case 'balance':
            if (type === 'all') {
              aValue = a.balance || 0
              bValue = b.balance || 0
            } else if (type === 'bed') {
              aValue = a.M_Bed || 0
              bValue = b.M_Bed || 0
            } else {
              aValue = a.M_Bes || 0
              bValue = b.M_Bes || 0
            }
            break
          default:
            return 0
        }
        
        if (order === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })
    }
    
    // Sorting handlers
    const sortBedBy = (field) => {
      if (bedSortField.value === field) {
        bedSortOrder.value = bedSortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        bedSortField.value = field
        bedSortOrder.value = 'desc'
      }
    }
    
    const sortBesBy = (field) => {
      if (besSortField.value === field) {
        besSortOrder.value = besSortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        besSortField.value = field
        besSortOrder.value = 'desc'
      }
    }
    
    const sortAllBy = (field) => {
      if (allSortField.value === field) {
        allSortOrder.value = allSortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        allSortField.value = field
        allSortOrder.value = 'desc'
      }
    }
    
    // Pagination computed properties
    const paginatedBedData = computed(() => {
      const start = (bedCurrentPage.value - 1) * itemsPerPage
      return filteredBedData.value.slice(start, start + itemsPerPage)
    })
    
    const bedTotalPages = computed(() => {
      return Math.ceil(filteredBedData.value.length / itemsPerPage)
    })
    
    const paginatedBesData = computed(() => {
      const start = (besCurrentPage.value - 1) * itemsPerPage
      return filteredBesData.value.slice(start, start + itemsPerPage)
    })
    
    const besTotalPages = computed(() => {
      return Math.ceil(filteredBesData.value.length / itemsPerPage)
    })
    
    const paginatedAllData = computed(() => {
      const start = (allCurrentPage.value - 1) * itemsPerPage
      return filteredAllData.value.slice(start, start + itemsPerPage)
    })
    
    const allTotalPages = computed(() => {
      return Math.ceil(filteredAllData.value.length / itemsPerPage)
    })
    
    // Pagination handlers
    const bedPrevPage = () => bedCurrentPage.value > 1 && bedCurrentPage.value--
    const bedNextPage = () => bedCurrentPage.value < bedTotalPages.value && bedCurrentPage.value++
    const besPrevPage = () => besCurrentPage.value > 1 && besCurrentPage.value--
    const besNextPage = () => besCurrentPage.value < besTotalPages.value && besCurrentPage.value++
    const allPrevPage = () => allCurrentPage.value > 1 && allCurrentPage.value--
    const allNextPage = () => allCurrentPage.value < allTotalPages.value && allCurrentPage.value++
    
    // Customer functions
    const getBalanceClass = (customer) => {
      const balance = customer.balance || 0
      if (balance > 0) return 'bed-amount'
      if (balance < 0) return 'bes-amount'
      return ''
    }
    
    const getBalanceTypeClass = (customer) => {
      const balance = customer.balance || 0
      if (balance > 0) return 'type-badge-bed'
      if (balance < 0) return 'type-badge-bes'
      return 'type-badge-zero'
    }
    
    const getBalanceTypeText = (customer) => {
      const balance = customer.balance || 0
      if (balance > 0) return 'بدهکار'
      if (balance < 0) return 'بستانکار'
      return 'صفر'
    }
    
    const getCustomerTotalBalance = (customer) => {
      const bed = customer.M_Bed || 0
      const bes = customer.M_Bes || 0
      return bed - bes
    }
    
    // View customer details
    const viewCustomerDetails = (customerId) => {
      // Find customer in all data
      const customer = allCustomersData.value.find(c => c.Ei5 === customerId)
      if (customer) {
        selectedCustomer.value = customer
        showCustomerModal.value = true
      }
    }
    
    const closeCustomerModal = () => {
      showCustomerModal.value = false
      selectedCustomer.value = null
    }
    
    // Export functions
    const printReport = () => {
      // Implementation for printing report
      console.log('Printing report...')
      window.print()
    }
    
    const exportToExcel = () => {
      // Implementation for exporting to Excel
      console.log('Exporting to Excel...')
      // You can use libraries like SheetJS (xlsx) here
    }
    
    // Navigation
    const navigateToDashboard = () => {
      router.push('/employer/dashboard')
    }
    
    // Format functions
    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(amount)
    }
    
    // Watch for currency changes
    watch(selectedCurrency, () => {
      // Reset loaded reports
      loadedReports.value = { bed: false, bes: false, all: false }
      
      // Reload current tab
      loadReportData(true, activeTab.value)
    })


    const getCurrencyName = (code) => {
      if (!code || code === 0 || code === "0") {
        return 'ریال';
      }
      
      const currency = arzList.value.find(arz => arz.idd == code);
      return currency ? currency.nam : 'ریال';
    };

    // Handle tab click with lazy loading
    const handleTabClick = async (tab) => {
      // If clicking the same tab, do nothing
      if (tab === activeTab.value) return
      
      // Change active tab immediately for better UX
      activeTab.value = tab
      
      // Load data for the new tab if not already loaded
      await loadReportData(false, tab)
    }

    // Refresh current tab
    const refreshCurrentTab = async () => {
      await loadReportData(true, activeTab.value)
    }
    
    // Initialize
    onMounted(async () => {
      await fetchAllArz()
      await loadReportData(false, 'bed')
    })
    
    return {
      // Data
      loading,
      loadingArz,
      error,
      searchQuery,
      activeTab,
      bedReport,
      besReport,
      allCustomersData,
      totalBalance,
      handleTabClick,
      refreshCurrentTab,
      
      // UI states
      arzList,
      selectedCurrency,
      showCustomerModal,
      selectedCustomer,
      getCurrencyName,
      
      // Pagination
      bedCurrentPage,
      besCurrentPage,
      allCurrentPage,
      paginatedBedData,
      paginatedBesData,
      paginatedAllData,
      bedTotalPages,
      besTotalPages,
      allTotalPages,
      itemsPerPage,
      
      // Sorting
      bedSortField,
      bedSortOrder,
      besSortField,
      besSortOrder,
      allSortField,
      allSortOrder,

      currencyPrices,
      currencyPricesLoading,
      getTodayRate,
      getCurrencyNameFromId,
      
      // Methods
      loadReportData,
      onSearch,
      sortBedBy,
      sortBesBy,
      sortAllBy,
      bedPrevPage,
      bedNextPage,
      besPrevPage,
      besNextPage,
      allPrevPage,
      allNextPage,
      getBalanceClass,
      getBalanceTypeClass,
      getBalanceTypeText,
      getCustomerTotalBalance,
      viewCustomerDetails,
      closeCustomerModal,
      printReport,
      exportToExcel,
      navigateToDashboard,
      formatCurrency,
      
      // Computed
      filteredBedData,
      filteredBesData,
      filteredAllData,
      getBalanceStatusClass,
      getBalanceStatusText,
      getTotalBalanceClass
    }
  }
}
</script>

<style scoped>
.bank-app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  direction: rtl;
  padding: 10px;
  background: var(--bg);
  color: var(--text);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.4rem;
  color: var(--heading);
  margin: 0;
  font-weight: 600;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 5px 15px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  color: var(--primary);
  margin-left: 15px;
}

.back-arrow:hover {
  background-color: var(--hover);
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-1);
  flex-wrap: wrap;
  border: 1px solid var(--card-border);
}

.refresh-btn, .print-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.refresh-btn {
  background: var(--primary);
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--primary-hover);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.print-btn {
  background: var(--header-1);
  color: white;
}

.print-btn:hover {
  background: var(--header-2);
}

.export-btn {
  background: var(--green);
  color: white;
}

.export-btn:hover {
  background: var(--orange-2);
}

/* Currency Selector */
.currency-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.currency-label {
  font-weight: 500;
  color: var(--subtext);
}

.currency-select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--card-bg);
  font-size: 14px;
  min-width: 150px;
  color: var(--text);
}

.currency-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Search Container */
.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
  transition: border-color 0.3s;
  background: var(--card-bg);
  color: var(--text);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--subtext);
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.summary-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px var(--shadow-1);
  border-top: 4px solid;
  border: 1px solid var(--card-border);
  border-top-width: 4px;
}

.bed-summary {
  border-top-color: var(--orange-1);
}

.bes-summary {
  border-top-color: var(--green);
}

.total-summary {
  border-top-color: var(--primary);
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.summary-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--heading);
}

.summary-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.bed-badge {
  background: var(--orange-1);
}

.bes-badge {
  background: var(--green);
}

.total-badge {
  background: var(--primary);
}

.summary-amount {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
}

.bed-summary .summary-amount {
  color: var(--orange-1);
}

.bes-summary .summary-amount {
  color: var(--green);
}

.summary-count, .summary-status {
  font-size: 14px;
  color: var(--subtext);
}

.balance-status-bed {
  color: var(--orange-1);
  font-weight: 500;
}

.balance-status-bes {
  color: var(--green);
  font-weight: 500;
}

.balance-status-zero {
  color: var(--subtext);
  font-weight: 500;
}

/* Report Tabs */
.report-tabs {
  background: var(--card-bg);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.tab-buttons {
  display: flex;
  background: var(--bg);
  border-bottom: 1px solid var(--card-border);
}

.tab-button {
  flex: 1;
  padding: 15px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: var(--subtext);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background-color: var(--hover);
  color: var(--text);
}

.tab-button.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
  background-color: var(--card-bg);
}

.tab-count {
  font-size: 13px;
  opacity: 0.8;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--subtext);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--card-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--orange-1);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.error-state h4 {
  margin: 0 0 10px 0;
  color: var(--heading);
}

.error-state p {
  color: var(--subtext);
  margin-bottom: 20px;
}

.retry-btn {
  padding: 10px 25px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background: var(--primary-hover);
}

/* Report Content */
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--card-border);
}

.report-header h4 {
  margin: 0;
  color: var(--heading);
  font-size: 18px;
}

.report-info {
  color: var(--subtext);
  font-size: 14px;
}

/* Customer Table */
.customer-table-container {
  overflow-x: auto;
}

.customer-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.customer-table th {
  background: var(--bg);
  padding: 12px 15px;
  text-align: right;
  font-weight: 600;
  color: var(--heading);
  border-bottom: 2px solid var(--card-border);
  white-space: nowrap;
}

.customer-table td {
  padding: 12px 15px;
  border-bottom: 1px solid var(--card-border);
  vertical-align: middle;
  color: var(--text);
}

.customer-table tbody tr:hover {
  background-color: var(--hover);
}

.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.sortable:hover {
  background-color: var(--hover);
}

.sort-indicator {
  margin-right: 5px;
  font-size: 12px;
}

/* Customer Info */
.customer-name {
  font-weight: 500;
  color: var(--heading);
}

.customer-code {
  font-size: 12px;
  color: var(--subtext);
  margin-right: 5px;
}

.contact-info {
  font-size: 13px;
  color: var(--text);
}

.mobile-info {
  display: block;
  color: var(--subtext);
  font-size: 12px;
}

/* Amount Cells */
.amount-cell {
  font-weight: 600;
  white-space: nowrap;
}

.bed-amount {
  color: var(--orange-1);
}

.bes-amount {
  color: var(--green);
}

.days-cell {
  text-align: center;
  font-weight: 500;
}

/* Transaction Count */
.transaction-count {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  margin: 0 2px;
}

.bed-count {
  background: rgba(231, 76, 60, 0.1);
  color: var(--orange-1);
}

.bes-count {
  background: rgba(39, 174, 96, 0.1);
  color: var(--green);
}

/* Type Badge */
.type-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.type-badge-bed {
  background: rgba(231, 76, 60, 0.1);
  color: var(--orange-1);
}

.type-badge-bes {
  background: rgba(39, 174, 96, 0.1);
  color: var(--green);
}

.type-badge-zero {
  background: rgba(127, 140, 141, 0.1);
  color: var(--muted);
}

/* Details Button */
.details-btn {
  padding: 6px 15px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.3s;
}

.details-btn:hover {
  background: var(--primary-hover);
}

/* Pagination */

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--subtext);
  font-size: 16px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px var(--shadow-2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--card-border);
}

.modal-header h3 {
  margin: 0;
  color: var(--heading);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--subtext);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: var(--hover);
}

.modal-body {
  padding: 20px;
}

.customer-details {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.detail-section h4 {
  margin: 0 0 15px 0;
  color: var(--heading);
  font-size: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--primary);
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-label {
  font-size: 13px;
  color: var(--subtext);
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: var(--text);
  text-align: right;
  font-weight: 500;
}

/* Balance Grid */
.balance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.balance-item {
  background: var(--bg);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid;
}

.bed-balance {
  border-left-color: var(--orange-1);
}

.bes-balance {
  border-left-color: var(--green);
}

.total-balance {
  border-left-color: var(--primary);
}

.balance-label {
  display: block;
  font-size: 13px;
  color: var(--subtext);
  margin-bottom: 8px;
}

.balance-value {
  font-size: 20px;
  font-weight: 700;
}

.bed-balance .balance-value {
  color: var(--orange-1);
}

.bes-balance .balance-value {
  color: var(--green);
}

.balance-currency {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-top: 5px;
}

/* Stats Grid */
.stats-grid {
  display: flex;
  gap: 30px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stat-label {
  font-size: 13px;
  color: var(--subtext);
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--heading);
}

/* Modal Footer */
.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--card-border);
  text-align: left;
}

.close-modal-btn {
  padding: 10px 30px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.close-modal-btn:hover {
  background: var(--primary-hover);
}

/* Customer Cards Container */
.customer-cards-container {
  padding: 20px 0;
}

.customer-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

@media (max-width: 768px) {
  .customer-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .customer-cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Customer Card */
.customer-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 12px var(--shadow-1);
  border: 1px solid var(--card-border);
  transition: all 0.3s ease;
  overflow: hidden;
  height: 100%;
}

.customer-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px var(--shadow-2);
  border-color: var(--primary);
}

/* Card Body */
.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Rows */
.row {
  display: flex;
  align-items: center;
  padding: 1px 0;
}

.row:last-child {
  border-bottom: none;
}

/* First Row */
.first-row {
  display: flex;
  align-items: center;
}

.info-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 14px;
  color: var(--subtext);
  font-weight: 500;
  min-width: 40px;
}

.info-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--heading);
}

/* Second Row */
.second-row {
  background: var(--bg);
  border-radius: 8px;
  padding: 10px 5px;
  border: 1px solid var(--card-border);
}

.amount-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.amount-label {
  font-size: 15px;
  color: var(--text);
  font-weight: 500;
}

.amount-value {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.bed-amount {
  color: var(--orange-1);
}

.bes-amount {
  color: var(--green);
}

.amount-status {
  font-size: 14px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}

.status-bed {
  background: rgba(231, 76, 60, 0.1);
  color: var(--orange-1);
}

.status-bes {
  background: rgba(39, 174, 96, 0.1);
  color: var(--green);
}

/* Third Row */
.third-row {
  justify-content: center;
}

.rate-section {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg);
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px dashed var(--card-border);
}

.rate-label {
  font-size: 14px;
  color: var(--subtext);
  font-weight: 500;
}

.rate-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}

.duration-section {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.duration-item {
  display: inline;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 5px;
  background: var(--bg);
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.duration-label {
  font-size: 13px;
  color: var(--subtext);
  font-weight: 500;
}

.duration-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.duration-item:first-child .duration-value {
  color: var(--orange-1);
}

.duration-item:last-child .duration-value {
  color: var(--green);
}

.loading-rate {
  color: var(--muted);
  font-style: italic;
  font-size: 14px;
}

/* Card Header for Customer Cards */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, var(--bg) 0%, var(--bg) 100%);
  border-bottom: 1px solid var(--card-border);
}

.customer-id {
  display: flex;
  align-items: center;
  gap: 8px;
}

.id-label {
  font-size: 13px;
  color: var(--subtext);
  font-weight: 500;
}

.id-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--heading);
  background: var(--card-bg);
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid var(--card-border);
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-details-btn {
  background: var(--primary);
  color: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;
}

.card-details-btn:hover {
  background: var(--primary-hover);
  transform: scale(1.1);
}

/* Customer Name Section */
.customer-name-section {
  text-align: center;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--card-border);
}

.customer-name {
  margin: 0 0 8px 0;
  color: var(--heading);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
}

.customer-code {
  display: inline-block;
  font-size: 13px;
  color: var(--subtext);
  background: var(--bg);
  padding: 4px 12px;
  border-radius: 12px;
  border: 1px solid var(--card-border);
}

/* Card Footer */
.card-footer {
  padding: 15px 20px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
  text-align: center;
}

.view-details-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.view-details-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-2);
}

/* Sort Controls */
.sort-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: auto;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid var(--card-border);
  border-radius: 6px;
  background: var(--card-bg);
  font-size: 14px;
  min-width: 140px;
  direction: rtl;
  color: var(--text);
}

.sort-order-btn {
  padding: 8px 15px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.sort-order-btn:hover {
  background: var(--primary-hover);
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-container {
    width: 100%;
  }
  
  .summary-cards {
    grid-template-columns: 1fr;
  }
  
  .report-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .customer-table {
    font-size: 13px;
  }
  
  .customer-table th,
  .customer-table td {
    padding: 8px 10px;
  }
  
  .balance-grid,
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .modal-content {
    width: 95%;
  }
  
  .sort-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .sort-select {
    flex-grow: 1;
  }
}

@media (max-width: 480px) {
  .card-header {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .customer-id {
    justify-content: center;
  }
  
  .card-actions {
    justify-content: center;
  }
  
  .balance-item {
    flex-direction: column;
    align-items: stretch;
    gap: 5px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* Print Styles */
@media print {
  .action-bar,
  .summary-cards,
  .tab-buttons,
  .details-btn,
  .pagination-controls,
  .modal-overlay {
    display: none !important;
  }
  
  .bank-app {
    padding: 0;
    background: white;
  }
  
  .customer-table {
    border: 1px solid #000;
  }
  
  .customer-table th {
    background: #ddd !important;
    -webkit-print-color-adjust: exact;
  }
}
</style>